import { useState } from 'react';
import OpenAI from 'openai';
import { toast } from 'react-hot-toast';

// Validate API key
const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
if (!apiKey) {
  console.warn('OpenAI API key is missing');
}

const openai = new OpenAI({
  apiKey,
  dangerouslyAllowBrowser: true, // Note: In production, proxy through backend
});

const SYSTEM_PROMPT = `You are an AI assistant for Bidzora, a government contracting platform. 
Your role is to help users with:
- Understanding government contracting processes
- Navigating the platform features
- Writing and reviewing proposals
- Understanding compliance requirements
- Finding relevant opportunities

Be concise, professional, and helpful. If you're unsure about something, say so.
Always prioritize accuracy and compliance in your responses.`;

export function useOpenAI() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendMessage = async (message: string): Promise<string> => {
    if (!apiKey) {
      throw new Error('OpenAI API key is not configured');
    }

    setLoading(true);
    setError(null);

    try {
      const response = await openai.chat.completions.create({
        model: 'gpt-4-turbo-preview',
        messages: [
          { role: 'system', content: SYSTEM_PROMPT },
          { role: 'user', content: message }
        ],
        temperature: 0.7,
        max_tokens: 500,
      });

      const content = response.choices[0]?.message?.content;
      if (!content) {
        throw new Error('No response generated');
      }

      return content;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to get response from AI';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return { sendMessage, loading, error };
}